import { faEye, faEyeSlash } from "@fortawesome/free-regular-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useEffect, useRef, useState } from "react"
import 'react-calendar/dist/Calendar.css'
import 'react-clock/dist/Clock.css'
import DateTimePicker from 'react-datetime-picker'
import 'react-datetime-picker/dist/DateTimePicker.css'
import { ReactComponent as CalendarIcon } from "../assets/images/icons/ic-calendar.svg"
import { ReactComponent as SearchIcon } from "../assets/images/icons/ic-search.svg"
import { filterAndSortArray, filterArray } from "../utils"
import styles from "./TextInput.module.css"
import typo from "../typography.module.css"

const TextInput = ({
    type,
    defaultValue,
    value,
    placeholder,
    label,
    onKeyUp = () => { },
    onCommit = () => { },
    autocomplete,
    suggestions,
    style = {},
    disabled = false,
    numberType = "int",
    decimals = 2,
    min = null,
    max = null,
    error = false,
    errorMessage = "",
    slideToControl = false,
    onFocus = () => { }
}) => {

    const normalizeValue = (input, fallback) => {
        if (input === null || input === undefined) {
            return fallback;
        }
        if (typeof input === 'number' && Number.isNaN(input)) {
            return fallback;
        }
        return input;
    };

    const [inputType, setInputType] = useState(type ?? 'text')
    const [icon, setIcon] = useState(faEye)
    const [innerValue, setInnerValue] = useState(() => {
        if (type === "date") {
            return normalizeValue(value, null)
        }
        return normalizeValue(value, "")
    })
    const [showSuggestions, setShowSuggestions] = useState(false)
    const [isFocused, setIsFocused] = useState(false)
    const [helperText, setHelperText] = useState("")
    const helperTextTimeoutRef = useRef(null)

    // Slide to control state
    const dragStartX = useRef(null);
    const dragStartValue = useRef(null);
    const dragging = useRef(false);
    const inputRef = useRef(null);

    const ref = useRef(null);

    const emitFocusChange = (isFocusedState) => {
        if (typeof onFocus === 'function') {
            onFocus(isFocusedState);
        }
    };
    useEffect(() => {
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (type === "date") {
            setInnerValue(normalizeValue(value, null))
        } else {
            setInnerValue(normalizeValue(value, ""))
        }
    }, [value, type])

    useEffect(() => {
        if (type === "date" && defaultValue) {
            setInnerValue(defaultValue)
        }
    }, [type, defaultValue])

    useEffect(() => {
        if (helperText) {
            if (helperTextTimeoutRef.current) {
                clearTimeout(helperTextTimeoutRef.current)
            }
            helperTextTimeoutRef.current = setTimeout(() => setHelperText(""), 5000)
        }

        return () => {
            if (helperTextTimeoutRef.current) {
                clearTimeout(helperTextTimeoutRef.current)
            }
        }
    }, [helperText])

    const debounceTimeout = useRef(null);

    const handleOnKeyUp = async (event) => {
        setInnerValue(event.target.value)

        if (event.target.value.length > 0) {
            setShowSuggestions(true)
        }
        else {
            setShowSuggestions(false)
        }

        clearTimeout(debounceTimeout.current);
        debounceTimeout.current = setTimeout(async () => {
            if (onKeyUp && typeof onKeyUp === 'function') {
                await onKeyUp(event.target.value);
            }
        }, 300);

    }

    const handleOnKeyUpNumeric = (event) => {
        const rawValue = event.target.value;

        const isTransientValue = rawValue.trim() === '' || rawValue === '-' || rawValue === '.' || rawValue === '-.';
        if (isTransientValue) {
            setInnerValue(rawValue);
            setHelperText("");
            onKeyUp?.(null, rawValue);
            return;
        }

        const sanitizedValue = rawValue.replace(',', '.').replace(/[^0-9.-]/g, '');
        let numericValue = numberType === "int"
            ? parseInt(sanitizedValue, 10)
            : parseFloat(sanitizedValue);

        if (Number.isNaN(numericValue)) {
            setInnerValue(rawValue);
            onKeyUp?.(null, rawValue);
            return;
        }

        if (numberType === "float") {
            numericValue = parseFloat(numericValue.toFixed(decimals));
        }

        // Troncamento del valore se ci sono più decimali di quelli consentiti
        if (numberType === "float" && rawValue.includes('.')) {
            const [integerPart, decimalPart] = rawValue.split('.');
            if (decimalPart && decimalPart.length > decimals) {
                const truncatedValue = `${integerPart}.${decimalPart.slice(0, decimals)}`;
                setInnerValue(truncatedValue);
                onKeyUp?.(parseFloat(truncatedValue), truncatedValue);
                return;
            }
        }

        if ((min !== null && min !== undefined) && !isNaN(numericValue) && numericValue < min) {
            setHelperText(`Valore minimo: ${min}`);
        } else if ((max !== null && max !== undefined) && !isNaN(numericValue) && numericValue > max) {
            setHelperText(`Valore massimo: ${max}`);
        } else {
            setHelperText("");
        }

        setInnerValue(rawValue);
        onKeyUp?.(numericValue, rawValue);
    };

    // Slide to control handlers
    const handleMouseDown = (e) => {
        if (!slideToControl || disabled) return;
        dragging.current = true;
        dragStartX.current = e.clientX;
        const parsedStart = numberType === 'int' ? parseInt(innerValue) : parseFloat(innerValue);
        dragStartValue.current = (innerValue === '' || isNaN(parsedStart)) ? 0 : parsedStart;
        document.body.style.cursor = 'ew-resize';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleMouseMove = (e) => {
        if (!dragging.current) return;
        const deltaX = e.clientX - dragStartX.current;
        const DEADZONE = 10; // attiva il controllo solo dopo i primi 10px
        const absDelta = Math.abs(deltaX);
        // Non applicare alcuna modifica finché non si supera la deadzone
        if (absDelta < DEADZONE) return;

        // Dopo la deadzone, calcola il moltiplicatore in base al movimento residuo
        const adjustedDelta = absDelta - DEADZONE;
        // 1 step per 10px oltre la deadzone (10-19px => 1, 20-29px => 2, ...)
        let multiplier = Math.floor(adjustedDelta / 10) + 1;
        let step = numberType === 'int' ? 1 : 0.1;
        let valueChange = step * multiplier * Math.sign(deltaX);
        let newValue = dragStartValue.current + valueChange;
        if (numberType === 'int') {
            newValue = Math.round(newValue);
        } else {
            newValue = parseFloat(newValue.toFixed(decimals));
        }
        if (min !== null && newValue < min) newValue = min;
        if (max !== null && newValue > max) newValue = max;
        setInnerValue(newValue.toString());
        onKeyUp(newValue, newValue.toString());
    };

    const handleMouseUp = () => {
        if (!dragging.current) return;
        dragging.current = false;
        document.body.style.cursor = '';
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
    };

    const showPassword = () => {
        if (inputType === 'password') {
            setInputType('text')
            setIcon(faEyeSlash)
        }
        else {
            setInputType('password')
            setIcon(faEye)
        }
    }

    function matchSuggestion() {
        return filterArray(suggestions, innerValue).length > 0
    }


    const handleClickOutside = (e) => {
        if (ref.current && !ref.current.contains(e.target)) {
            setShowSuggestions(false)
        }
    }

    const handleChangeDate = (nextValue) => {
        if (nextValue === null || nextValue === undefined) {
            setInnerValue(null)
            onKeyUp?.(null)
            return
        }

        if (nextValue instanceof Date) {
            const safeDate = new Date(nextValue.getTime())
            setInnerValue(safeDate)
            onKeyUp?.(safeDate)
            return
        }

        // When user is typing, react-datetime-picker returns the raw string, keep it as-is
        setInnerValue(nextValue)
    }

    const handleKeyDown = (e) => {
        if (e.key === "Enter") {
            onCommit(e.target.value)
        }
    }

    return (
        <>
            {type === 'password' &&
                <div className={`${styles.container} ${error ? styles.error : ''}`}>
                    <input
                        value={innerValue ?? ''}
                        type={inputType}
                        className={[styles.roundedInput, styles.passwordInput].join((' '))}
                        placeholder={placeholder}
                        onChange={handleOnKeyUp}
                        onKeyUp={handleOnKeyUp}
                        autoComplete={autocomplete}
                        onFocus={() => emitFocusChange(true)}
                        onBlur={() => emitFocusChange(false)}
                    />
                    <FontAwesomeIcon icon={icon} className={styles.showPassword} onClick={showPassword} />
                </div>
            }
            {type === 'text' &&
                <div className={`${styles.container} ${error ? styles.error : ''}`}>
                    {error &&
                        <div className={typo.caption} style={{ color: "var(--sf-red)", paddingBottom: '4px' }}>
                            *{errorMessage}
                        </div>
                    }
                    {innerValue && <div className={styles.label}> {label} </div>}
                    <input
                        disabled={disabled}
                        value={innerValue}
                        type={inputType}
                        className={styles.roundedInput}
                        placeholder={placeholder}
                        onChange={handleOnKeyUp}
                        onKeyUp={handleOnKeyUp}
                        onKeyDown={handleKeyDown}
                        autoComplete={autocomplete}
                        onFocus={() => { setIsFocused(true); emitFocusChange(true); }}
                        onBlur={() => { setIsFocused(false); emitFocusChange(false); }}
                        style={{ ...style }}></input>
                    {showSuggestions && suggestions && matchSuggestion() &&
                        <div className={styles.suggestions} ref={ref}>
                            {filterAndSortArray(suggestions, innerValue)
                                .map(sugg => {
                                    return (
                                        <button key={sugg} className={styles.suggestion}
                                            onClick={() => {
                                                setInnerValue(sugg)
                                                setShowSuggestions(false)
                                                if (onKeyUp && typeof onKeyUp === 'function') {
                                                    onKeyUp(sugg)
                                                }
                                                if (onCommit && typeof onCommit === 'function') {
                                                    onCommit(sugg)
                                                }
                                            }}>{sugg}</button>
                                    )
                                })}
                        </div>
                    }
                </div>
            }
            {type === 'search' &&
                <div className={styles.container}>
                    <div className={styles.searchContainer} style={style}>
                        <input
                            value={innerValue ?? ''}
                            type={inputType}
                            className={styles.searchInput}
                            placeholder={placeholder}
                            onChange={handleOnKeyUp}
                            autoComplete={autocomplete}
                            onFocus={() => emitFocusChange(true)}
                            onBlur={() => emitFocusChange(false)}
                        />
                    </div>
                    <SearchIcon className={styles.searchIcon} />
                </div>
            }
            {type === 'number' &&
                <div style={{ position: 'relative' }}>
                    {error &&
                        <div className={typo.caption} style={{ color: "var(--sf-red)", paddingBottom: '4px' }}>
                            *{errorMessage}
                        </div>
                    }
                    {innerValue && <div className={styles.label}> {label} </div>}
                    <input
                        ref={inputRef}
                        style={{ ...style, userSelect: slideToControl ? 'none' : undefined }}
                        value={innerValue ?? ''}
                        inputMode={numberType === "float" ? "decimal" : "numeric"}
                        type='text'
                        className={`${styles.roundedInput} ${error ? styles.error : ''}`}
                        placeholder={placeholder}
                        onChange={handleOnKeyUpNumeric}
                        onKeyUp={handleOnKeyUpNumeric}
                        autoComplete={autocomplete}
                        disabled={disabled}
                        onMouseDown={slideToControl ? handleMouseDown : undefined}
                        onFocus={() => emitFocusChange(true)}
                        onBlur={() => emitFocusChange(false)}
                    />
                    {
                        helperText &&
                        <div className={typo.caption} style={{ color: "var(--sf-red)" }}>{helperText}</div>
                    }
                </div>
            }
            {
                type === "date" &&
                <div className={styles.container} style={style}>
                    {
                        placeholder &&
                        <div className={styles.date}>
                            <div className={styles.placeholder}>{placeholder} </div>
                            <DateTimePicker
                                format="d/M/yyyy HH:mm"
                                disabled={disabled}
                                className={styles.dateTimePicker}
                                onChange={handleChangeDate}
                                value={innerValue}
                                calendarIcon={<CalendarIcon />}
                                minDate={min}
                                maxDate={max}
                                locale="it-IT"
                                onFocus={() => emitFocusChange(true)}
                                onBlur={() => emitFocusChange(false)}
                            />
                        </div>

                    }

                    {
                        !placeholder &&
                        <DateTimePicker
                            format="d/M/yyyy HH:mm"
                            ref={ref}
                            disabled={disabled}
                            className={styles.dateTimePicker}
                            onChange={handleChangeDate}
                            value={innerValue}
                            calendarIcon={<CalendarIcon />}
                            minDate={min}
                            maxDate={max}
                            locale="it-IT"
                            onFocus={() => emitFocusChange(true)}
                            onBlur={() => emitFocusChange(false)}
                        />
                    }
                </div>
            }

        </>
    )

}

export default TextInput
